/**
 * SabaTransactionsTable — admin ledger view over `/api/admin/saba/transactions`.
 *
 * Filters: user_id, type, currency, from/to (ISO date strings).
 * Pagination: 50/page, prev/next; total-count driven by the API.
 * Amount coloring: debit → red (negative on screen), credit / rollback → green.
 * `vendorTransId` is truncated with a copy-to-clipboard affordance so
 *   operators can grep it from partner tickets without exposing the
 *   full 128-char id in the table.
 */
import { useCallback, useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Copy, Loader2 } from "lucide-react";

import { Badge } from "../ui-r1/Badge";
import Button from "../ui-r1/Button";
import Input from "../ui-r1/Input";
import { Card, CardBody } from "../ui-r1/Card";
import { getAdminSabaTransactions } from "../../lib/sabaApi";
import { extractError } from "../../lib/formatError";

const PAGE_SIZE = 50;
const CURRENCIES = ["", "BDT", "INR", "PKR", "USDT"];
const TYPES = ["", "debit", "credit", "rollback"];

// Color chip per row type — visually mirrors the balance direction.
const TYPE_TONE = {
  debit: "danger",
  credit: "success",
  rollback: "gold",
};


export default function SabaTransactionsTable() {
  const [filters, setFilters] = useState({
    user_id: "", type: "", currency: "", from: "", to: "",
  });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchPage = useCallback(async (opts = {}) => {
    setLoading(true);
    setErr(null);
    try {
      const params = { ...filters, limit: PAGE_SIZE, offset };
      // Support callers who want to override without stomping filters.
      Object.assign(params, opts);
      const res = await getAdminSabaTransactions(params);
      setData(res);
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setOffset(0);
    fetchPage({ offset: 0 });
  };

  const total = data.total || 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <Card data-testid="saba-txn-card">
      <CardBody>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Transactions</div>
            <div className="font-display italic text-2xl text-ink">SABA ledger</div>
          </div>
          <div className="text-[10px] tracking-widest uppercase text-ink-3">
            {total} total
          </div>
        </div>

        {/* Filters */}
        <form
          onSubmit={applyFilters}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4"
          data-testid="saba-txn-filters"
        >
          <Input
            type="text" placeholder="User ID"
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
            data-testid="saba-txn-filter-user"
          />
          <SelectField
            testid="saba-txn-filter-type"
            value={filters.type}
            onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
            options={TYPES}
            placeholder="All types"
          />
          <SelectField
            testid="saba-txn-filter-currency"
            value={filters.currency}
            onChange={(v) => setFilters((f) => ({ ...f, currency: v }))}
            options={CURRENCIES}
            placeholder="All currencies"
          />
          <Input
            type="date" value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            data-testid="saba-txn-filter-from"
          />
          <div className="flex gap-2">
            <Input
              type="date" value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              data-testid="saba-txn-filter-to"
              className="flex-1"
            />
            <Button type="submit" variant="secondary" data-testid="saba-txn-apply">
              Apply
            </Button>
          </div>
        </form>

        {/* Table */}
        <div className="rounded-lg border border-line overflow-x-auto">
          <table className="w-full text-sm" data-testid="saba-txn-table">
            <thead className="bg-highest text-[10px] tracking-widest uppercase text-ink-3">
              <tr>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-right px-3 py-2">Balance</th>
                <th className="text-left px-3 py-2">Vendor tx</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-ink-3">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={8} className="text-center py-8 text-danger" data-testid="saba-txn-error">
                  {err}
                </td></tr>
              )}
              {!loading && !err && data.items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-ink-3" data-testid="saba-txn-empty">
                  No SABA transactions yet.
                </td></tr>
              )}
              {!loading && !err && data.items.map((it) => (
                <TxnRow key={it._id} row={it} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Paging */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-[10px] tracking-widest uppercase text-ink-3">
            {total === 0 ? "0 of 0" : `${from}–${to} of ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              data-testid="saba-txn-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              data-testid="saba-txn-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}


function TxnRow({ row }) {
  const amountSigned = row.type === "debit" ? -Math.abs(row.amount) : Math.abs(row.amount);
  const amountClass =
    row.type === "debit" ? "text-danger"
    : row.type === "credit" ? "text-success"
    : "text-gold";
  return (
    <tr
      className="border-t border-line hover:bg-highest/50"
      data-testid={`saba-txn-row-${row._id}`}
    >
      <td className="px-3 py-2 text-ink-2 whitespace-nowrap">
        {formatDate(row.created_at)}
      </td>
      <td className="px-3 py-2 text-ink">
        {row.username || <span className="text-ink-3">—</span>}
      </td>
      <td className="px-3 py-2">
        <Badge variant={TYPE_TONE[row.type] || "muted"}>
          {row.type}
        </Badge>
      </td>
      <td className="px-3 py-2 text-ink-2">{row.currency}</td>
      <td className={"px-3 py-2 text-right font-mono " + amountClass}>
        {amountSigned > 0 ? "+" : ""}{amountSigned.toFixed(2)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-ink-2 whitespace-nowrap">
        {fmt(row.balance_before)} → {fmt(row.balance_after)}
      </td>
      <td className="px-3 py-2">
        <CopyableId value={row.vendorTransId} testid={`saba-txn-vtxn-${row._id}`} />
      </td>
      <td className="px-3 py-2">
        <Badge variant={row.status === "applied" ? "success" : row.status === "rolled_back" ? "gold" : "muted"}>
          {row.status}
        </Badge>
      </td>
    </tr>
  );
}


function CopyableId({ value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-ink-3">—</span>;
  const short = value.length > 16 ? value.slice(0, 8) + "…" + value.slice(-4) : value;
  const onCopy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] text-ink-2 hover:text-gold transition"
      title={value}
      data-testid={testid}
    >
      {short}
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3 text-ink-3" />}
    </button>
  );
}


function SelectField({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 px-2 rounded-md border border-line bg-highest text-sm text-ink capitalize focus:outline-none focus:border-gold"
      data-testid={testid}
    >
      {options.map((o) =>
        o === "" ? (
          <option key="all" value="">{placeholder}</option>
        ) : (
          <option key={o} value={o}>{o}</option>
        )
      )}
    </select>
  );
}


function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toFixed(2);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "2-digit", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
